import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { CustomTable, ITableHeaderCell } from '../../../../components/CustomTable/table';
import { tips } from '../../../../constants/tips';
import { clearCurrencyTransfersAction } from '../../../../ducks/transactions/action';
import { getCurrencyTransfersListSelector } from '../../../../ducks/transactions/selector';
import { getCurrencyTransactionsListThunk } from '../../../../ducks/transactions/thunk';
import { useAppParams } from '../../../../hooks/useAppParams';
import { usePageUpdater } from '../../../../hooks/usePageUpdater';
import { usePagination } from '../../../../hooks/usePagination';
import { useSignalHook } from '../../../../hooks/useSignalHook';
import { ThunkAppDispatch } from '../../../../store';
import { ICurrencyTransfer } from '../../../../types/currency';
import { CurrencyTransferRow } from './CurrencyTransferRow';

const headersListCurrency: ITableHeaderCell[] = [
  {
    name: 'Transaction ID',
    tooltip: tips.transaction,
  },
  {
    name: 'Amount',
    tooltip: tips.amountAPL,
  },
  {
    name: 'Code',
  },
  {
    name: 'Age',
    tooltip: tips.timestamp,
  },
  {
    name: 'Age (UTC)',
    tooltip: tips.timestampUTC,
  },
  {
    name: 'Sender Wallet',
    tooltip: tips.sender,
  },
  {
    name: 'Recipient Wallet',
    tooltip: tips.recipientRS,
  },
];

export const CurrencyTable = () => {
  const params = useAppParams();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { clearSignals, createSignal, removeCurrentSignal } = useSignalHook();

  const dispatch = useDispatch<ThunkAppDispatch>();
  const currencyTransfersList = useSelector(getCurrencyTransfersListSelector, shallowEqual);
  const { setNextPage, setPrevPage, page, resetPagination, firstIndex, lastIndex } = usePagination({
    defaultPage: 1,
  });

  const getDataLoader = useCallback(() => {
    // if we don't have code we need all transactions and we don't need to send this request
    if (!params.code) return;

    const controller = createSignal();
    dispatch(
      getCurrencyTransactionsListThunk(
        {
          firstIndex,
          lastIndex,
          code: params.code,
        },
        {
          signal: controller.signal,
        },
      ),
    )
      .then((res) => {
        if (res !== 'cancel request') {
          setIsLoading(false);
        }
      })
      .finally(() => {
        removeCurrentSignal(controller);
      });
  }, [dispatch, firstIndex, lastIndex, params.code, createSignal, removeCurrentSignal]);

  useEffect(() => {
    resetPagination();
    setIsLoading(true);
    dispatch(clearCurrencyTransfersAction());
  }, [location.pathname, resetPagination, dispatch]);

  useEffect(() => {
    clearSignals();
  }, [location.pathname, firstIndex, lastIndex, clearSignals]);

  usePageUpdater(getDataLoader);

  if (!params.code) return null;

  return (
    <CustomTable<ICurrencyTransfer>
      tableTitle={`${params.code ?? ''} Currency Transfers`}
      tableHeaders={headersListCurrency}
      data={currencyTransfersList ?? []}
      TableRow={CurrencyTransferRow}
      page={page}
      onPaginationNext={setNextPage}
      onPaginationPrev={setPrevPage}
      emptyMessage='No transactions found.'
      isLoading={isLoading || !currencyTransfersList}
    />
  );
};
